import { useState, useEffect } from 'react';
import { settingsApi } from '../../api/settings';
import SmtpSettings from './SmtpSettings';
import GraphSettings from './GraphSettings';
import Button from '../common/Button';
import Spinner from '../common/Spinner';
import { useUIStore } from '../../stores/uiStore';

/**
 * Email Provider Settings Component
 * Unified component for managing email provider configuration (SMTP or Microsoft Graph)
 */
export default function EmailProviderSettings({ settings, onChange, onTest, connectionStatus, connectionError, onStatusChange }) {
  const [providerStatus, setProviderStatus] = useState(null);
  const [loadingStatus, setLoadingStatus] = useState(true);
  const [selectedProvider, setSelectedProvider] = useState('auto');
  const [graphConnectionStatus, setGraphConnectionStatus] = useState(null);
  const [graphConnectionError, setGraphConnectionError] = useState(null);
  const { showToast } = useUIStore();

  useEffect(() => {
    loadProviderStatus();
  }, []);

  useEffect(() => {
    // Update selected provider based on settings
    if (settings?.email_provider?.provider) {
      setSelectedProvider(settings.email_provider.provider);
    }
  }, [settings]);

  const loadProviderStatus = async () => {
    try {
      const response = await settingsApi.getEmailProviderStatus();
      setProviderStatus(response.data);
    } catch (error) {
      console.error('Failed to load provider status:', error);
    } finally {
      setLoadingStatus(false);
    }
  };

  const handleProviderChange = (provider) => {
    setSelectedProvider(provider);
    // Update the email_provider category with the provider selection
    // The backend expects 'email.provider' to be saved under email_provider category
    onChange({
      ...settings,
      email_provider: {
        ...settings.email_provider,
        provider: provider
      }
    });
  };

  const handleSmtpChange = (smtp) => {
    onChange({ ...settings, smtp });
  };

  const handleGraphChange = (graphSettings) => {
    // Map frontend field names to backend expected names
    // Frontend: graph_tenant_id -> Backend: graph.tenant_id (but stored as tenant_id under email_provider)
    const mappedSettings = {};
    Object.keys(graphSettings).forEach(key => {
      // Remove 'graph_' prefix if present, as backend stores without prefix under email_provider category
      const backendKey = key.startsWith('graph_') ? key : key;
      mappedSettings[backendKey] = graphSettings[key];
    });
    
    onChange({
      ...settings,
      email_provider: {
        ...settings.email_provider,
        ...mappedSettings
      }
    });
  };

  const handleSmtpTestConnection = async () => {
    try {
      const provider = selectedProvider === 'auto' ? providerStatus?.active_method : selectedProvider;
      
      if (!provider || provider === 'auto') {
        showToast('Please select a specific email provider (SMTP or Microsoft Graph)', 'error');
        return;
      }

      if (onStatusChange) {
        onStatusChange('testing', null);
      }
      
      // Call backend to test connection - server fetches config from database
      // Pass config if available (allows testing unsaved changes), otherwise empty object
      const config = settings?.smtp || {};
      const response = await settingsApi.testSmtp(config);
      
      if (response.data.success) {
        // Build credential source message
        const source = response.data.credential_source;
        let sourceMsg = 'Using credentials from: ';
        if (source.password === 'database') {
          sourceMsg += 'Database (password), Request (host, port, user)';
        } else {
          sourceMsg += 'Request (all fields)';
        }
        
        showToast(`✓ SMTP connection successful! ${sourceMsg}`, 'success');
        if (onStatusChange) {
          onStatusChange('connected', null);
        }
      } else {
        showToast('Connection test failed', 'error');
        if (onStatusChange) {
          onStatusChange('failed', response.data.error);
        }
      }
    } catch (error) {
      console.error('Test connection failed:', error);
      showToast(`Connection test failed: ${error.response?.data?.message || error.message}`, 'error');
      if (onStatusChange) {
        onStatusChange('failed', {
          type: 'unknown',
          message: error.response?.data?.message || error.message
        });
      }
    }
  };

  const handleGraphTestConnection = async () => {
    try {
      const provider = selectedProvider === 'auto' ? providerStatus?.active_method : selectedProvider;
      
      if (!provider || provider === 'auto') {
        showToast('Please select a specific email provider (SMTP or Microsoft Graph)', 'error');
        return;
      }

      const config = settings.email_provider;
      
      setGraphConnectionStatus('testing');
      setGraphConnectionError(null);
      
      // Call backend to test connection only (no email)
      const response = await settingsApi.testEmailProvider({
        provider: 'graph',
        config: config,
        connection_only: true
      });
      
      if (response.data.success) {
        showToast(`✓ Microsoft Graph connection successful!`, 'success');
        setGraphConnectionStatus('connected');
      } else {
        showToast('Graph connection test failed', 'error');
        setGraphConnectionStatus('failed');
        setGraphConnectionError(response.data.error);
      }
    } catch (error) {
      console.error('Graph connection test failed:', error);
      showToast(`Connection test failed: ${error.response?.data?.message || error.message}`, 'error');
      setGraphConnectionStatus('failed');
      setGraphConnectionError(error.response?.data?.error || {
        type: 'unknown',
        message: error.response?.data?.message || error.message
      });
    }
  };

  const handleSmtpTestEmail = async () => {
    try {
      const provider = selectedProvider === 'auto' ? providerStatus?.active_method : selectedProvider;
      
      if (!provider || provider === 'auto') {
        showToast('Please select a specific email provider (SMTP or Microsoft Graph)', 'error');
        return;
      }

      // No need to pass config - server fetches from database
      await onTest('smtp');
    } catch (error) {
      console.error('Test email failed:', error);
    }
  };

  const handleGraphTestEmail = async () => {
    try {
      const provider = selectedProvider === 'auto' ? providerStatus?.active_method : selectedProvider;
      
      if (!provider || provider === 'auto') {
        showToast('Please select a specific email provider (SMTP or Microsoft Graph)', 'error');
        return;
      }

      // No need to pass config - server fetches from database
      await onTest('graph');
    } catch (error) {
      console.error('Test email failed:', error);
    }
  };

  if (loadingStatus) {
    return (
      <div className="flex justify-center py-8">
        <Spinner />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Provider Status Banner */}
      {providerStatus && (
        <div className={`rounded-lg p-4 ${
          providerStatus.is_initialized 
            ? 'bg-green-50 border border-green-200' 
            : 'bg-yellow-50 border border-yellow-200'
        }`}>
          <div className="flex items-center">
            <div className="flex-shrink-0">
              {providerStatus.is_initialized ? (
                <svg className="h-5 w-5 text-green-400" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                </svg>
              ) : (
                <svg className="h-5 w-5 text-yellow-400" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                </svg>
              )}
            </div>
            <div className="ml-3">
              <h3 className={`text-sm font-medium ${
                providerStatus.is_initialized ? 'text-green-800' : 'text-yellow-800'
              }`}>
                {providerStatus.is_initialized ? '✓ Email Service Active' : '⚠ Email Service Not Configured'}
              </h3>
              <div className={`mt-1 text-sm ${
                providerStatus.is_initialized ? 'text-green-700' : 'text-yellow-700'
              }`}>
                <p>
                  <strong>Active Provider:</strong> {providerStatus.provider_name || 'None'}
                  {providerStatus.source && (
                    <span className="ml-2 text-xs">
                      (configured via {providerStatus.source.smtp || providerStatus.source.graph})
                    </span>
                  )}
                </p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Provider Selection */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Email Provider
        </label>
        <select
          value={selectedProvider}
          onChange={(e) => handleProviderChange(e.target.value)}
          className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500"
        >
          <option value="auto">Auto-detect (based on configuration)</option>
          <option value="smtp">SMTP (Standard Email Server)</option>
          <option value="graph">Microsoft Graph API (Office 365)</option>
        </select>
        <p className="mt-1 text-sm text-gray-500">
          {selectedProvider === 'auto' && 'System will automatically choose the best configured provider'}
          {selectedProvider === 'smtp' && 'Use standard SMTP protocol for sending emails'}
          {selectedProvider === 'graph' && 'Use Microsoft Graph API for Office 365 email sending'}
        </p>

        {/* Database Configuration Status Badges */}
        {providerStatus && (
          <div className="mt-3 flex gap-4 text-sm">
            <div className="flex items-center gap-2">
              <span className={`inline-block w-2 h-2 rounded-full ${
                providerStatus.configuration?.smtp?.configured ? 'bg-green-500' : 'bg-gray-300'
              }`} />
              <span className={providerStatus.configuration?.smtp?.configured ? 'text-gray-700' : 'text-gray-400'}>
                SMTP: {providerStatus.configuration?.smtp?.configured ? 'Configured in DB' : 'Not Configured'}
              </span>
            </div>
            <div className="flex items-center gap-2">
              <span className={`inline-block w-2 h-2 rounded-full ${
                providerStatus.configuration?.graph?.configured ? 'bg-green-500' : 'bg-gray-300'
              }`} />
              <span className={providerStatus.configuration?.graph?.configured ? 'text-gray-700' : 'text-gray-400'}>
                Graph: {providerStatus.configuration?.graph?.configured ? 'Configured in DB' : 'Not Configured'}
              </span>
            </div>
          </div>
        )}
      </div>

      {/* Provider-specific Configuration */}
      {(selectedProvider === 'smtp' || selectedProvider === 'auto') && (
        <div className={selectedProvider === 'auto' ? 'opacity-75' : ''}>
          <SmtpSettings
            settings={settings?.smtp || {}}
            onChange={handleSmtpChange}
            onTestConnection={handleSmtpTestConnection}
            onTestEmail={handleSmtpTestEmail}
            connectionStatus={connectionStatus}
            connectionError={connectionError}
          />
        </div>
      )}

      {(selectedProvider === 'graph' || selectedProvider === 'auto') && (
        <div className={`${selectedProvider === 'auto' ? 'opacity-75 mt-8' : ''}`}>
          <GraphSettings
            settings={settings?.email_provider || {}}
            onChange={handleGraphChange}
            onTestConnection={handleGraphTestConnection}
            onTestEmail={handleGraphTestEmail}
            connectionStatus={graphConnectionStatus}
            connectionError={graphConnectionError}
          />
        </div>
      )}

      {selectedProvider === 'auto' && (
        <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
          <p className="text-sm text-gray-600">
            <strong>Note:</strong> In auto-detect mode, both SMTP and Microsoft Graph configurations are shown. 
            The system will use Microsoft Graph if configured, otherwise fall back to SMTP.
          </p>
        </div>
      )}
    </div>
  );
}